import React, { useState, useEffect } from "react";
import { Section } from "../StructureElements/Section";
import { SectionHeaderTypography } from "../Typographies/SectionHeaderTypography";
import { ExplainTypography } from "../Typographies/ExplainTypography";
import { ManufacturerAutoComplete } from "../autocompletes/manufacturer-auto.complete";
import { Manufacturer } from "../../../core/models/entities/Manufacturer";
import { OrganizationManufacturerAccessDto } from "../../../data/models/responses/OrganizationManufacturerAccessDto";
import { useApiClient } from "../../../data/clients/ApiClient";
import { useSuccessToast, useErrorToast } from "../Toast/Toast";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { Switch } from "@mui/material";
import { SectionHeader } from "../../screens/my-profile/SectionHeader";
import { SectionContent } from "../../screens/my-profile/SectionContent";

interface ManufacturerAccessControlProps {
  organizationId: number;
  manufacturers: Manufacturer[];
}

export function ManufacturerAccessControl({
  organizationId,
  manufacturers,
}: ManufacturerAccessControlProps) {
  const apiClient = useApiClient();
  const [accessData, setAccessData] =
    useState<OrganizationManufacturerAccessDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // State for user's current selections during configuration
  const [selectedManufacturerIds, setSelectedManufacturerIds] = useState<
    number[]
  >([]);
  const [currentAccessMode, setCurrentAccessMode] = useState<
    "Whitelist" | "Blacklist"
  >("Blacklist");

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadAccessData();
  }, [organizationId]);

  const loadAccessData = async () => {
    try {
      // Fetch both the organization data (for access mode) and manufacturer access data
      const [organizationData, manufacturerAccessData] = await Promise.all([
        apiClient.getOrganization(organizationId),
        apiClient.getOrganizationManufacturerAccess(organizationId),
      ]);

      const accessMode = organizationData.manufacturerAccessMode ?? "Blacklist";
      const savedManufacturers = manufacturerAccessData.manufacturers || [];

      // Ensure ALL manufacturers are included with correct hasAccess values
      const allManufacturers = manufacturers.map((manufacturer) => {
        const savedAccess = savedManufacturers.find(
          (m) => m.id === manufacturer.id
        );

        if (savedAccess) {
          // Use the saved access data
          return {
            ...savedAccess,
            name: manufacturer.name,
            isRestricted: manufacturer.isRestricted,
          };
        } else {
          // For manufacturers without saved access, determine default hasAccess based on access mode
          // In Whitelist mode: default hasAccess = false (not allowed)
          // In Blacklist mode: default hasAccess = true (allowed)
          const defaultHasAccess = accessMode === "Blacklist";

          return {
            id: manufacturer.id,
            name: manufacturer.name,
            hasAccess: defaultHasAccess,
            isRestricted: manufacturer.isRestricted,
          };
        }
      });

      // Combine the data with the access mode from the organization
      const processedData = {
        accessMode: accessMode,
        manufacturers: allManufacturers,
      };

      setAccessData(processedData);
      setCurrentAccessMode(accessMode);

      // Set initial selected manufacturers based on access mode and hasAccess values
      const initialSelectedIds: number[] = [];
      allManufacturers.forEach((manufacturer) => {
        if (accessMode === "Whitelist") {
          // In Whitelist mode: select manufacturers where hasAccess=true
          if (manufacturer.hasAccess) {
            initialSelectedIds.push(manufacturer.id);
          }
        } else {
          // In Blacklist mode: select manufacturers where hasAccess=false
          if (!manufacturer.hasAccess) {
            initialSelectedIds.push(manufacturer.id);
          }
        }
      });

      setSelectedManufacturerIds(initialSelectedIds);
    } catch (error) {
      console.error("Error loading access data:", error);
      useErrorToast("Failed to load manufacturer access settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAccessMode = () => {
    const newMode =
      currentAccessMode === "Blacklist" ? "Whitelist" : "Blacklist";
    setCurrentAccessMode(newMode);

    // When switching modes, clear selections to avoid confusion
    setSelectedManufacturerIds([]);
    setHasChanges(true);
  };

  const handleManufacturerSelect = async (
    manufacturer: Manufacturer | null
  ) => {
    if (!manufacturer) return;

    // Add manufacturer to selected list
    setSelectedManufacturerIds((prev) => [...prev, manufacturer.id]);
    setHasChanges(true);
  };

  const handleRemoveManufacturer = (manufacturerId: number) => {
    setSelectedManufacturerIds((prev) =>
      prev.filter((id) => id !== manufacturerId)
    );
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!accessData) return;

    setIsSaving(true);
    try {
      // First, update the access mode
      await apiClient.updateOrganizationAccessMode(
        organizationId,
        currentAccessMode
      );

      // Get current access data to compare
      const currentAccessData =
        await apiClient.getOrganizationManufacturerAccess(organizationId);

      // For ALL manufacturers, set the correct hasAccess value based on the logic:
      // - Whitelist mode: selected manufacturers get hasAccess=true, non-selected get hasAccess=false
      // - Blacklist mode: selected manufacturers get hasAccess=false, non-selected get hasAccess=true
      const allManufacturers = [...manufacturers];

      for (const manufacturer of allManufacturers) {
        const isSelected = selectedManufacturerIds.includes(manufacturer.id);

        let shouldHaveAccess: boolean;
        if (currentAccessMode === "Whitelist") {
          // Whitelist: selected = hasAccess true, non-selected = hasAccess false
          shouldHaveAccess = isSelected;
        } else {
          // Blacklist: selected = hasAccess false, non-selected = hasAccess true
          shouldHaveAccess = !isSelected;
        }

        // Find current access status for this manufacturer
        const currentAccess = (currentAccessData.manufacturers || []).find(
          (m) => m.id === manufacturer.id
        );

        // Only update if the access status needs to change
        if (!currentAccess || currentAccess.hasAccess !== shouldHaveAccess) {
          await apiClient.updateOrganizationManufacturerAccess(
            organizationId,
            manufacturer.id,
            shouldHaveAccess
          );
        }
      }

      useSuccessToast("Changes saved successfully");
      setHasChanges(false);
    } catch (error) {
      useErrorToast("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    loadAccessData(); // This will reset both accessData and selectedManufacturerIds
    setHasChanges(false);
  };

  if (isLoading) {
    return <div></div>;
  }

  if (!accessData) {
    return <div></div>;
  }

  // Get manufacturers that are currently selected (to display as chips)
  const selectedManufacturers = manufacturers.filter((manufacturer) =>
    selectedManufacturerIds.includes(manufacturer.id)
  );

  // Available manufacturers are those that can be added to the current list
  // Show ALL manufacturers except: 1) restricted manufacturers, 2) already selected manufacturers
  const availableManufacturers = manufacturers.filter((manufacturer) => {
    // Always exclude restricted manufacturers from being selectable
    if (manufacturer.isRestricted) {
      return false;
    }

    // Exclude manufacturers that are already selected
    const isAlreadySelected = selectedManufacturerIds.includes(manufacturer.id);

    return !isAlreadySelected;
  });

  return (
    <Section>
      <SectionHeader
        isCloseable={true}
        initialState={isOpen}
        onToggle={(open) => setIsOpen(open)}
      >
        <SectionHeaderTypography>Manufacturers</SectionHeaderTypography>
      </SectionHeader>

      <SectionContent close={!isOpen}>
        <div className={"flex-1 overflow-y-scroll space-y-4 mb-10"}>
          {/* Access Mode Toggle */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Whitelist</span>
              <Switch
                checked={currentAccessMode === "Blacklist"}
                onChange={handleToggleAccessMode}
                color="primary"
                size="small"
              />
              <span className="text-sm font-medium">Blacklist</span>
            </div>
          </div>
          <div>
            <ExplainTypography>
              {currentAccessMode === "Blacklist"
                ? "Selected manufacturers will not be visible to this Vendor:"
                : "Selected manufacturers will be the ONLY allowed manufacturers for this Vendor:"}
            </ExplainTypography>
            {/* Manufacturer Selection */}
            <div className="mt-2 w-72">
              <ManufacturerAutoComplete
                manufacturers={availableManufacturers}
                onSelected={handleManufacturerSelect}
              />
            </div>
          </div>

          {/* Selected Manufacturers List */}
          {selectedManufacturers.length > 0 && (
            <div className="flex flex-wrap mt-2 w-72">
              {selectedManufacturers.map((manufacturer) => (
                <ManufacturerChip
                  key={manufacturer.id}
                  manufacturer={manufacturer}
                  onRemove={() => handleRemoveManufacturer(manufacturer.id)}
                />
              ))}
            </div>
          )}
          {/* Save/Cancel Buttons */}
          <div className="flex space-x-2">
            <PrimaryButton onClick={handleSaveChanges} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </PrimaryButton>
            <SecondaryButton onClick={handleCancelChanges}>
              Cancel
            </SecondaryButton>
          </div>
        </div>
      </SectionContent>
    </Section>
  );
}

interface ManufacturerChipProps {
  manufacturer: { id: number; name: string; isRestricted?: boolean };
  onRemove: () => void;
}

function ManufacturerChip({ manufacturer, onRemove }: ManufacturerChipProps) {
  return (
    <div className="text-xs bg-gray-100 rounded px-2 py-1 mr-2 mb-2 flex items-center space-x-1">
      <span>{manufacturer.name}</span>
      {manufacturer.isRestricted && (
        <span className="text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
          Restricted
        </span>
      )}
      <button
        onClick={onRemove}
        className="text-gray-500 hover:text-gray-700"
        title="Remove from list"
      >
        ×
      </button>
    </div>
  );
}
